import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { api } from '../api';

function AnimatedCounter({ target, suffix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const duration = 2000;
    const step = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [isInView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

function ManifestoLine({ children, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      className="ab-manifesto-line"
      initial={{ opacity: 0, y: 60, clipPath: 'inset(100% 0 0 0)' }}
      animate={isInView ? { opacity: 1, y: 0, clipPath: 'inset(0% 0 0 0)' } : {}}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  const [artistCount, setArtistCount] = useState(0);
  const [trackCount, setTrackCount] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.8], [1, 0.95]);

  useEffect(() => {
    api.getArtists().then((data) => {
      const artists = data.artists || [];
      setArtistCount(artists.length);
      setTrackCount(artists.reduce((s, a) => s + (a.top_tracks?.length || 0), 0));
    }).catch(() => {});
  }, []);

  return (
    <div className="ab-page" data-testid="about-page">

      {/* === HERO — Full viewport statement === */}
      <motion.section className="ab-hero" ref={heroRef} style={{ opacity: heroOpacity, scale: heroScale }}>
        <video
          className="ab-hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        >
          <source
            src="https://lofimusic.sgp1.digitaloceanspaces.com/videos/hero-about.webm"
            type="video/webm"
          />
        </video>
        <div className="ab-hero-overlay" />
        <div className="ab-hero-inner">
          <motion.span
            className="ab-hero-tag"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Est. 2025 &mdash; Independent
          </motion.span>
          <motion.h1
            className="ab-hero-title"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            We don&rsquo;t follow<br />
            <span className="ab-hero-accent">the algorithm.</span><br />
            We follow the sound.
          </motion.h1>
        </div>
        <div className="ab-hero-scroll-hint">
          <motion.div
            className="ab-scroll-line"
            animate={{ scaleY: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </motion.section>

      {/* === MANIFESTO — big statement lines === */}
      <section className="ab-manifesto">
        <div className="ab-manifesto-inner">
          <ManifestoLine index={0}>
            <span className="ab-m-num">01</span>
            <h2>Sound as delicate as threads of a cloud,</h2>
          </ManifestoLine>
          <ManifestoLine index={1}>
            <span className="ab-m-num">02</span>
            <h2>woven together into something larger,</h2>
          </ManifestoLine>
          <ManifestoLine index={2}>
            <span className="ab-m-num">03</span>
            <h2>softer, and endlessly evolving.</h2>
          </ManifestoLine>
        </div>
      </section>

      {/* === STORY — editorial layout === */}
      <section className="ab-story">
        <div className="ab-story-inner">
          <motion.div
            className="ab-story-left"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
          >
            <span className="ab-story-label">Our Story</span>
            <h2 className="ab-story-heading">
              Born from<br />a simple<br /><span>observation.</span>
            </h2>
          </motion.div>
          <motion.div
            className="ab-story-right"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <p className="ab-story-dropcap">
              In an increasingly noisy world, people crave sonic spaces that invite
              stillness and reflection. CloudfiMusic was founded to answer that need &mdash;
              an independent label that champions the art of lofi music, far from the
              pressures of major labels and fleeting trends.
            </p>
            <p>
              From bedroom producers sharing ambient loops to established artists crafting
              cinematic soundscapes, every artist on our roster brings a unique texture.
              Together, we form a catalog that speaks to anyone seeking calm in the chaos.
            </p>
            <p>
              We handle distribution, promotion, and brand development so our artists
              can focus on what they do best &mdash; creating beautiful music. Independence
              means creative freedom, and that&rsquo;s what we offer every artist who
              joins CloudfiMusic.
            </p>
          </motion.div>
        </div>
      </section>

      {/* === STATS — animated numbers === */}
      <section className="ab-stats">
        <div className="ab-stats-inner">
          <div className="ab-stat">
            <span className="ab-stat-num"><AnimatedCounter target={artistCount} /></span>
            <span className="ab-stat-label">Artists on Roster</span>
          </div>
          <div className="ab-stat-divider" />
          <div className="ab-stat">
            <span className="ab-stat-num"><AnimatedCounter target={trackCount} /></span>
            <span className="ab-stat-label">Tracks Released</span>
          </div>
          <div className="ab-stat-divider" />
          <div className="ab-stat">
            <span className="ab-stat-num"><AnimatedCounter target={2025} /></span>
            <span className="ab-stat-label">Year Founded</span>
          </div>
          <div className="ab-stat-divider" />
          <div className="ab-stat">
            <span className="ab-stat-num"><AnimatedCounter target={100} suffix="%" /></span>
            <span className="ab-stat-label">Independent</span>
          </div>
        </div>
      </section>

      {/* === COMPANY — clean elevated === */}
      <section className="ab-company">
        <div className="ab-company-inner">
          <motion.div
            className="ab-company-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="ab-company-tag">The Label</span>
            <h2 className="ab-company-title">
              Operated by<br /><span>Xu&acirc;n Di&#7879;u Media</span>
            </h2>
          </motion.div>

          <div className="ab-company-grid">
            <motion.div className="ab-company-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0 }}>
              <span className="ab-cc-label">Company</span>
              <span className="ab-cc-value">Xu&acirc;n Di&#7879;u Media Co., Ltd</span>
              <span className="ab-cc-sub">C&ocirc;ng ty TNHH MTV Xu&acirc;n Di&#7879;u Media</span>
            </motion.div>
            <motion.div className="ab-company-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <span className="ab-cc-label">Representative</span>
              <span className="ab-cc-value">Xuan Chinh Nguyen</span>
            </motion.div>
            <motion.div className="ab-company-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <span className="ab-cc-label">Email</span>
              <a href="mailto:info@cloudfimusic.com" className="ab-cc-value ab-cc-link">info@cloudfimusic.com</a>
            </motion.div>
            <motion.div className="ab-company-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
              <span className="ab-cc-label">Company ID</span>
              <span className="ab-cc-value">0402222404</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === CTA === */}
      <section className="ab-cta">
        <motion.div
          className="ab-cta-inner"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="ab-cta-title">Want to become<br />part of us?</h2>
          <p className="ab-cta-text">
            If you craft lofi, ambient, or chill music and believe your sound<br />
            belongs here, we&rsquo;re always open to welcoming new artists to the roster.
          </p>
          <Link to="/contact" className="btn btn-primary" data-testid="about-contact-cta">
            Get in Touch <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
